import { registerDecorators as _registerDecorators, registerComponent as _registerComponent, LightningElement } from "lwc";
import _tmpl from "./userMenu.html";
import { WireStore } from '@oneappexchange/lwc-wired';
import equalityFn from 'zustand/shallow';
import { clearAuthenticationContext } from '~/modules/analytics/authenticationContext';
import { IS_DEVELOPMENT, TBID_MOCK_USER, TBID_URL } from '~/modules/config';
import { getUserLanguage } from '~/modules/helpers/languageHelper';
import { generateBaseUrl, logout } from '~/modules/helpers/routingHelper';
import { i18nStore, selectI18nModule } from '~/modules/stores/i18nStore';
import { userStore } from '~/modules/stores/userStore';
import { ENGLISH_LANGUAGE_CODE } from '~/modules/utilities/language_utils';
import { createCookie } from '~/modules/utilities/cookies';

/** Analytics menu identifier for user menu (parity with header mobile analytics). */
export const USER_MENU_IDENTIFIER = 'user menu';
class UserMenu extends LightningElement {
  constructor(...args) {
    super(...args);
    this.DEFAULT_BACKGROUND_IMAGE = '/images/arid-dunes.png';
    this.i18n = void 0;
    this.user = void 0;
    this._activeItem = 0;
    this._menuClassname = 'dropdown-menu';
    this._shouldFocusActiveItem = false;
    this._shouldFocusDropdown = false;
    this.expandedMenu = false;
    this.handleDocumentClick = event => {
      if (!this.expandedMenu) {
        return;
      }
      const dropdown = this.template?.querySelector('div[data-role="dropdown"]');
      const trigger = this.template?.querySelector('button[data-role="trigger"]');
      const composedPath = typeof event.composedPath === 'function' ? event.composedPath() : [];
      const target = event.target;
      const clickInsideDropdown = dropdown && composedPath.includes(dropdown) || !!dropdown && !!target && dropdown.contains(target);
      const clickInsideTrigger = trigger && composedPath.includes(trigger) || !!trigger && !!target && trigger.contains(target);
      if (!clickInsideDropdown && !clickInsideTrigger) {
        this.toogleMenu();
      }
    };
  }
  get menuItems() {
    let itemCounter = 1;
    const menuItems = [{
      id: 'profile',
      text: this.i18n.header.profile(),
      link: `${TBID_URL}/id`,
      active: itemCounter++ === this._activeItem
    }, {
      id: 'settings',
      text: this.i18n.header.settings(),
      link: `${TBID_URL}/settings`,
      active: itemCounter++ === this._activeItem
    }];

    // only certain users can see the My Installs and Subscriptions link
    if (getUserLanguage() === ENGLISH_LANGUAGE_CODE && this.user?.canCheckout && this.user?.canInstall) {
      menuItems.push({
        id: 'myInstalls',
        text: this.i18n.header.myInstalls(),
        link: `${generateBaseUrl()}/appxMyAccount`,
        active: itemCounter++ === this._activeItem
      });
    }
    menuItems.push({
      id: 'logout',
      text: this.i18n.header.logout(),
      link: '#',
      active: itemCounter++ === this._activeItem
    });
    return menuItems;
  }
  get backgroundImage() {
    return this.user?.backgroundImageUrl || this.DEFAULT_BACKGROUND_IMAGE;
  }
  get showUser() {
    return this.user?.tbidProfile?.active;
  }
  get profileName() {
    const tbidProfile = this.user?.tbidProfile;
    return this.i18n.shared.userName({
      FIRST_NAME: tbidProfile?.given_name ?? '',
      LAST_NAME: tbidProfile?.family_name ?? ''
    });
  }
  get profileImage() {
    return this.user?.tbidProfile?.picture ?? '';
  }
  get menuClassName() {
    return this._menuClassname + (this.expandedMenu ? ' expanded' : '');
  }
  connectedCallback() {
    document.addEventListener('mousedown', this.handleDocumentClick);
  }
  disconnectedCallback() {
    document.removeEventListener('mousedown', this.handleDocumentClick);
  }
  handleMenuItemClick(event) {
    const link = event.target?.closest?.('a');
    if (!link || link.getAttribute('data-testid') !== 'logout') {
      return;
    }
    event.preventDefault();
    if (IS_DEVELOPMENT && TBID_MOCK_USER) {
      userStore.reset();
      clearAuthenticationContext();
      createCookie('sid', '', -1);
      this.toogleMenu();
    } else {
      logout();
    }
  }

  // @ts-expect-error * Automated Type Error Fix
  handleAnalytics(event) {
    const linkClicked = event.target.querySelector('a');
    const clickText = linkClicked?.textContent?.toLowerCase() || USER_MENU_IDENTIFIER;
    event.detail.navLink = {
      menuItem: USER_MENU_IDENTIFIER,
      clickText: clickText,
      clickUrl: linkClicked?.href,
      destinationType: 'internal',
      ctaClick: false,
      navLevel: linkClicked ? 'l2' : 'l1'
    };
    event.detail.elementType = 'loginNavLink';
  }
  handleTriggerClick() {
    this.toogleMenu();
    this._shouldFocusDropdown = true;
  }
  toogleMenu() {
    this.expandedMenu = !this.expandedMenu;
    if (!this.expandedMenu) {
      this._activeItem = 0;
      this._shouldFocusActiveItem = false;
    }
  }

  // @ts-expect-error * Automated Type Error Fix
  handleTriggerKeyDown(event) {
    if (event.key === 'ArrowDown' || event.key === 'Enter') {
      this._activeItem = 1;
      this._shouldFocusActiveItem = true;
      this.toogleMenu();
      event.preventDefault();
    }
  }
  handleMenuKeyDown(event) {
    switch (event.key) {
      case 'Tab':
        if (event.shiftKey) {
          this.selectPreviousMenuItem();
        } else {
          this.selectNextMenuItem();
        }
        event.preventDefault();
        break;
      case 'Escape':
        this.toogleMenu();
        this.focusTrigger();
        break;
      case 'ArrowDown':
        this.selectNextMenuItem();
        event.preventDefault();
        break;
      case 'ArrowUp':
        this.selectPreviousMenuItem();
        event.preventDefault();
        break;
      case 'Enter':
        this.activateMenuItem();
        break;
      default:
        event.preventDefault();
    }
  }
  renderedCallback() {
    if (this._shouldFocusActiveItem) {
      this.focusActiveItem();
    }
    if (this._shouldFocusDropdown) {
      this.focusDropdown();
      this._shouldFocusDropdown = false;
    }
  }
  focusActiveItem() {
    this.template.querySelector('a[data-active="true"]').focus();
  }
  focusDropdown() {
    this.template.querySelector('div[data-role="dropdown"]').focus();
  }
  focusTrigger() {
    this.template.querySelector('button[data-role="trigger"]').focus();
  }
  selectNextMenuItem() {
    this._activeItem++;
    if (this._activeItem > this.menuItems.length) {
      this._activeItem = 1;
    }
  }
  selectPreviousMenuItem() {
    this._activeItem--;
    if (this._activeItem < 1) {
      this._activeItem = this.menuItems.length;
    }
  }
  activateMenuItem() {
    if (this._activeItem > 0) {
      this.template.querySelector('a[data-active="true"]').click();
    }
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(UserMenu, {
  wire: {
    i18n: {
      adapter: WireStore,
      dynamic: [],
      config: function ($cmp) {
        return {
          store: i18nStore,
          selector: selectI18nModule
        };
      }
    },
    user: {
      adapter: WireStore,
      dynamic: [],
      config: function ($cmp) {
        return {
          store: userStore,
          selector: state => ({
            canInstall: state.canInstall,
            canCheckout: state.canCheckout,
            tbidProfile: state.tbidProfile,
            backgroundImageUrl: state.backgroundImageUrl
          }),
          equalityFn
        };
      }
    }
  },
  fields: ["DEFAULT_BACKGROUND_IMAGE", "_activeItem", "_menuClassname", "_shouldFocusActiveItem", "_shouldFocusDropdown", "expandedMenu", "handleDocumentClick"]
});
const __lwc_component_class_internal = _registerComponent(UserMenu, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;